import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquareText, ArrowLeft, Clock, Trash2, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SavedConversationsPage = ({ user }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
    } catch (e) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      const token = localStorage.getItem('kaplie_token');
      await axios.delete(`${API}/conversations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(prev => prev.filter(c => c.id !== id));
      toast.success('Conversation deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diff < 604800000) {
      return d.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const filtered = conversations.filter(c =>
    c.contact_name.toLowerCase().includes(search.toLowerCase()) ||
    (c.preview || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="fixed inset-0 bg-gradient-to-br from-[#D4AF37]/[0.04] via-black to-violet-900/[0.06] pointer-events-none" />

      <nav className="relative z-10 border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard">
            <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5 text-white/60" />
            </button>
          </Link>
          <MessageSquareText className="w-5 h-5 text-[#D4AF37]" />
          <h1 className="text-lg font-semibold">Saved Conversations</h1>
          <span className="text-sm text-white/40 ml-auto">{conversations.length} saved</span>
        </div>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-6">
        {conversations.length > 3 && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search conversations..."
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/50"
              />
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-[#D4AF37] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquareText className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/40 mb-2">{search ? 'No matching conversations' : 'No saved conversations yet'}</p>
            <p className="text-white/30 text-sm">Turn on "Save Conversation" during your next call</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="conversations-list">
            {filtered.map((conv) => (
              <Link to={`/conversation/${conv.id}`} key={conv.id}>
                <div className="p-4 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all group mb-3" data-testid={`conversation-${conv.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37]/20 to-[#C5A059]/10 rounded-full flex items-center justify-center">
                        <span className="text-lg font-semibold text-white">
                          {conv.contact_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-white">{conv.contact_name}</p>
                        <div className="flex items-center gap-2 text-xs text-white/40">
                          <span>{formatDate(conv.started_at)}</span>
                          {conv.duration_seconds > 0 && (
                            <>
                              <span>-</span>
                              <Clock className="w-3 h-3" />
                              <span>{formatDuration(conv.duration_seconds)}</span>
                            </>
                          )}
                          <span>-</span>
                          <span>{conv.entry_count} messages</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => deleteConversation(conv.id, e)}
                      className="p-2 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded-lg transition-all"
                      data-testid={`delete-conversation-${conv.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>

                  {conv.preview && (
                    <p className="text-sm text-white/50 truncate ml-13 pl-13">{conv.preview}</p>
                  )}

                  {conv.summary && (
                    <div className="mt-2 p-2 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/10">
                      <p className="text-xs text-[#D4AF37]/70 line-clamp-2">{conv.summary}</p>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SavedConversationsPage;
